import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostListener, inject, signal, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { MenubarModule } from 'primeng/menubar';
import { TokenService } from '../../services/token.service';
import { originalAdminItems } from './utils/original-admin-items.util';
import { JwtPayload } from '../../models/jwt-payload.model';
import { AuthService } from '../../services/auth.service';
import { DropdownModel } from '../../models';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { UserFacilityService } from '../../../services';
import { filter, finalize } from 'rxjs';
import { pagesWithNoFacilityRequirement } from '../../../shared/consts/pages-with-no-facility-requirement';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalAuthService } from '../../../services/msal.service';
import { UserService } from '../../../services/user.service';
import { GlobalSearchComponent } from '../../../shared/components/global-search/global-search.component';
import { GlobalSearchResponseModel } from '../../models';
import { User } from '../../models/user.model';
import { UserAvatarComponent } from '../../../shared/components/user-avatar/user-avatar.component';
import { NotificationsComponent } from '../../../shared/components/notifications/notifications.component';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { Menu } from 'primeng/menu';

@Component({
  selector: 'app-menubar',
  standalone: true,
  imports: [
    CommonModule,
    MenubarModule,
    DropdownModule,
    FormsModule,
    GlobalSearchComponent,
    NotificationsComponent,
    UserAvatarComponent,
    MenuModule,
    ButtonModule,
  ],
  templateUrl: './menubar.component.html',
  styleUrl: './menubar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenubarComponent {
  items: MenuItem[] = [];
  baseItems: MenuItem[] = [];
  jwtPayload!: JwtPayload;
  profileItems: MenuItem[] = [];
  profileMenuItems: MenuItem[] = [];
  isMiddleScreen = signal(false);
  profileUser = signal<User | null>(null);
  profileLoaded = signal(false);
  profileMenuOpen = signal(false);
  
  @ViewChild('profileMenu') profileMenu?: Menu;
  @ViewChild('profileMenuButton') profileMenuButton?: HTMLElement;

  private _msalService = inject(MsalAuthService);
  private _userService = inject(UserService);
  private _tokenService = inject(TokenService);
  private _authService = inject(AuthService);
  private _userFacility = inject(UserFacilityService);
  private _router = inject(Router);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  public facilities = signal<DropdownModel[]>([]);
  public isAdmin = signal<boolean>(false);
  selectedFacility = this._authService.getCurrentFacility() || null;
  isEdit = false;
  destroyRef = inject(DestroyRef);

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const profileMenuElement = target.closest('.profile-menu-container');
    const profileDropdownElement = target.closest('.profile-menu-dropdown');
    
    // Close profile menu if clicking outside
    if (!profileMenuElement && !profileDropdownElement && this.profileMenuOpen()) {
      this.closeProfileMenu();
    }
  }

  ngOnInit() {
    this.initialize();
  }

  private initialize() {
    this.jwtPayload = this._tokenService.getJwtPayload();

    if (this.jwtPayload?.Permission?.length) {
      this.setupMenu();
      this.setupProfileMenu();
      this.loadProfileAvatar();
    }
    this.isAdmin.set(this._tokenService.isAdmin());

    this.setupFacilities();
    this.setupRouterEvents();
    this.checkScreenSize();
  }

  private setupMenu() {
    const permissions = this.jwtPayload.Permission.map((p) => p.toLowerCase());

    const cloned = this.deepClone(originalAdminItems);
    this.baseItems = this.filterMenuItemsPure(cloned, permissions);
    this.items = this.baseItems.filter(item => item.label !== 'Dashboard');
    this._changeDetectorRef.markForCheck();
  }

  private deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  private filterMenuItemsPure(items: any[], permissions: string[]): any[] {
    const mapped = items.map((it) => {
      const childItems = it.items ? this.filterMenuItemsPure(it.items, permissions) : undefined;
      return { ...it, items: childItems };
    });

    const filtered = mapped.filter((it) => {
      if (this.canAccessMenuItem(it, permissions)) {
        return true;
      }
      if (it.items && it.items.length > 0) {
        return true;
      }
      return false;
    });

    return filtered;
  }

  private canAccessMenuItem(item: any, permissions: string[]): boolean {
    if (!item.resource) {
      return true;
    }

    const normalizedResource = String(item.resource)
      .replaceAll(' ', '')
      .toLowerCase();

    if (permissions.includes(`${normalizedResource}showinmenu`)) {
      return true;
    }

    if (normalizedResource === 'recovercustomer') {
      return permissions.includes('recovercustomerread') || permissions.includes('recovercustomeredit');
    }

    if (normalizedResource === 'recoverquote') {
      return permissions.includes('recoverquoteread') || permissions.includes('recoverquoteedit');
    }

    if (normalizedResource === 'recover') {
      return (
        permissions.includes('recovercustomerread') ||
        permissions.includes('recovercustomeredit') ||
        permissions.includes('recoverquoteread') ||
        permissions.includes('recoverquoteedit')
      );
    }

    return false;
  }

  private setupProfileMenu() {
    this.profileItems = [
      {
        label: this.jwtPayload.unique_name,
        items: [
          { label: 'Profile', icon: 'pi pi-user', route: '/profile' },
          { label: 'Logout', icon: 'pi pi-power-off', command: () => this.logout() },
        ],
      },
    ];
    
    this.profileMenuItems = [
      { 
        label: 'Profile', 
        icon: 'pi pi-user', 
        command: () => {
          this._router.navigate(['/profile']);
        }
      },
      { 
        label: 'Logout', 
        icon: 'pi pi-power-off', 
        command: () => {
          this.logout();
        }
      },
    ];
  }

  private loadProfileAvatar() {
    this.profileLoaded.set(false);
    this._userService
      .getProfile<User>()
      .pipe(
        finalize(() => this.profileLoaded.set(true)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((user) => {
        this.profileUser.set(user);
      });
  }

  private setupFacilities() {
    this._userFacility
      .getUserFacilities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: DropdownModel[]) => {
        const list = [{ id: null, description: 'All Facilities' }, ...result];
        this.facilities.set(list);
        this.checkRouteForInputDisable();
        this._changeDetectorRef.markForCheck();
      });

    this._authService.refreshMenuFacility = (facilityIdentifier: string | null) => {
      this._authService.setCurrentFacility(facilityIdentifier);
      this.selectedFacility = facilityIdentifier;
      this.checkRouteForInputDisable();
      this._changeDetectorRef.markForCheck();
    };
  }

  private setupRouterEvents() {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e: any) => {
        const url = e?.urlAfterRedirects ?? this._router.url;

        localStorage.removeItem('previousUrl');

        const isHome = url === '/' || url.startsWith('/dashboard');
        if (isHome) {
          this.setupMenu();
        }

        this.checkRouteForInputDisable();
        this._changeDetectorRef.markForCheck();
      });
  }

  private checkScreenSize(): void {
    // PrimeNG menubar automatically shows hamburger when items don't fit
    const middle = window.innerWidth <= 1200;
    const changed = this.isMiddleScreen() !== middle;
    this.isMiddleScreen.set(middle);

    if (changed) {
      this._changeDetectorRef.markForCheck();
    }
  }

  private checkRouteForInputDisable(): void {
    const segments = this._router.url.split('/');

    this.isEdit = segments[2] === 'edit';

    if (!pagesWithNoFacilityRequirement.includes(segments[1])) {
      let facilities = this.facilities();
      if (segments[2] === 'new') {
        facilities = facilities.filter((x) => !!x.id);
        if (!this.selectedFacility && facilities.length > 0) {
          this.selectedFacility = facilities[0].id;
          this._authService.setCurrentFacility(this.selectedFacility);
        }
      } else if (!facilities.some((x) => x.id == null)) {
        facilities = [{ id: null, description: 'All Facilities' }, ...facilities];
      }
      this.facilities.set(facilities);
    }
  }

  public changeFacility(): void {
    this._authService.setCurrentFacility(this.selectedFacility);
    const urlSegments = this._router.url.split('/');
    const firstSegment = urlSegments[1];

    if (urlSegments[2] === 'new') {
      return;
    }

    this._router.navigate([`/${firstSegment}`]).then(() => {
      this.windowReload();
    });
  }

  /* istanbul ignore next */
  private windowReload() {
    if (!(window as any).__karma__) {
      window.location.reload();
    }
  }

  private async logout() {
    this._authService.logout();
    await this._msalService.logout();
  }

  toggleProfileMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const wasOpen = this.profileMenuOpen();
    this.profileMenuOpen.set(!wasOpen);
    
    if (!wasOpen) {
      // Calculate position for dropdown after it's rendered
      this._changeDetectorRef.detectChanges();
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('button');
        const dropdown = document.querySelector('.profile-menu-dropdown') as HTMLElement;
        if (button && dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.top = `${rect.bottom + 8}px`;
          dropdown.style.right = `${window.innerWidth - rect.right}px`;
        }
      }, 0);
    }
  }

  closeProfileMenu() {
    this.profileMenuOpen.set(false);
  }

  navigateToProfile() {
    this._router.navigate(['/profile']);
    this.closeProfileMenu();
  }

  handleLogout() {
    this.logout();
    this.closeProfileMenu();
  }

  public onGlobalSearch(searchResults: GlobalSearchResponseModel | null): void {
    if (searchResults) { 
    }
  }
}

